<template>
  <v-container>
    <FormToolbar
      class="mb-4"
      title="Update Tank Status"
      :onSubmitClick="submitStatus"
      :onCloseClick="onCloseClick"
      :isSubmitting="isSubmitting"
    />

    <ValidationObserver ref="form">
      <v-form class="mt-1">
        <v-container>
          <v-row>
            <v-col cols="12">
              <ValidationProvider
                name="Name"
                rules="required"
                v-slot="{ errors }"
              >
                <v-select
                  v-model="status.bulk"
                  label="Bulk"
                  :items="getBulksByType(status.tankBulkType)"
                  item-text="bulkName"
                  item-value="bulkName"
                  :error-messages="errors"
                >
                </v-select>
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="8" md="10">
              <v-slider
                v-model="status.quantityOnBoard"
                label="Quantity"
                class="align-center"
                :max="status.maxCapacity"
                :min="0"
                hide-details
                thumb-label="always"
                step="0.1"
              >
              </v-slider>
            </v-col>

            <v-col cols="12" sm="4" md="2">
              <ValidationProvider
                name="Quantity"
                rules="required"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="status.quantityOnBoard"
                  class="mt-0 pt-0"
                  hide-details
                  single-line
                  type="number"
                  :suffix="status.tankQtyUom"
                  step="0.1"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-dialog
                ref="statusDateMenu"
                v-model="statusDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="status.statusDate"
                    label="Status Date"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>

                <v-date-picker
                  v-model="status.statusDate"
                  @change="statusDateSelected()"
                  :max="currentDate"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="startDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>

            <v-col cols="6">
              <v-dialog
                ref="statusTimeMenu"
                v-model="statusTimeMenu"
                transition="scale-transition"
                max-width="300"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="status.statusTime"
                    label="Status Time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>

                <v-time-picker
                  v-if="statusTimeMenu"
                  v-model="status.statusTime"
                  format="24hr"
                  :max="currentTime"
                  full-width
                  @click:minute="statusTimeMenu = false"
                ></v-time-picker>
              </v-dialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <ValidationProvider
                name="Offshore Location"
                rules="max:100"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="status.offshoreLocation"
                  label="Offshore Location"
                  counter="100"
                  clearable
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <ValidationProvider
                name="Well Number"
                rules="max:50"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="status.wellNumber"
                  label="Well Number"
                  counter="50"
                  clearable
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="2">
              <v-checkbox v-model="status.isBackload" label="Is Backload" />
            </v-col>

            <v-col cols="12" md="5">
              <v-dialog
                ref="loadDateMenu"
                v-model="loadDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="status.loadDateLt"
                    label="Loaded Date"
                    readonly
                    v-on="on"
                    clearable
                  ></v-text-field>
                </template>

                <v-date-picker
                  v-model="status.loadDateLt"
                  no-title
                  scrollable
                  @change="loadDateMenu = false"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="loadDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="12" md="5">
              <v-dialog
                ref="dischargeDateMenu"
                v-model="dischargeDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="status.dischargeDateLt"
                    label="Discharge Date"
                    readonly
                    v-on="on"
                    clearable
                  ></v-text-field>
                </template>

                <v-date-picker
                  v-model="status.dischargeDateLt"
                  no-title
                  scrollable
                  @change="dischargeDateMenu = false"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dischargeDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <ValidationProvider
                name="Remarks"
                rules="max:500"
                v-slot="{ errors }"
              >
                <v-textarea
                  v-model="status.remarks"
                  label="Remarks"
                  counter="500"
                  :error-messages="errors"
                ></v-textarea>
              </ValidationProvider>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import DateTimeHelpers from '../../mixins/DateTimeHelpers';
import FormToolbar from '../ui/FormToolbar.vue';
import '../../validators';

export default {
  name: 'TankStatusForm',
  props: {
    status: Object,
    onSubmitClick: Function,
    onCloseClick: Function,
  },
  components: { FormToolbar, ValidationObserver, ValidationProvider },
  data() {
    return {
      isSubmitting: false,
      statusDateMenu: false,
      statusTimeMenu: false,
      loadDateMenu: false,
      dischargeDateMenu: false,
      currentDate: DateTimeHelpers.getCurrentDateLt(),
      currentTime: DateTimeHelpers.getCurrentTimeLt()
    };
  },
  computed: {
    ...mapGetters({
      getActiveSite: 'userStore/getActiveSite',
      getBulksByType: 'bulksStore/getBulksByType',
    }),
  },
  methods: {
    submitStatus() {
      this.$refs.form.validate().then((isValid) => {
        if (isValid) {
          this.isSubmitting = true;
          this.setStatusDateTimeValue();
          this.onSubmitClick().finally(() => {
            this.isSubmitting = false;
          });
        }
      });
    },
    setStatusDateTimeValue() {
      const { statusDate, statusTime } = this.status;
      this.status.statusDateTimeLt = DateTimeHelpers
        .dateAndTimeLtString(statusDate, statusTime);
    },
    statusDateSelected() {
      this.statusDateMenu = false;
      this.statusTimeMenu = true;
    },
  },
  created() {
    this.$store.dispatch('bulksStore/loadBulks', this.getActiveSite.id);
  },
};
</script>
